/**
 * OutstandingBalanceButton — Header CTA showing the user's DUE-NOW
 * balance only. Pre-entry-close pay-later seats are intentionally
 * excluded — those aren't enforceable yet and surfacing them in the
 * global header would nag the user about money that doesn't have to
 * move yet. The badge fires for:
 *   - Post-entry-close pending fees (the pay-later grace expired).
 *   - Supplemental rejection surcharges on already-paid rows
 *     (rejection happens, $25 extra is now owed RIGHT NOW).
 *   - Pending_payment rows whose membership claim was REJECTED — the
 *     rejection bumped non_member_fee onto payment_amount and that
 *     amount is owed immediately, even pre-entry-close.
 *
 * The Financials tab is where the gross liability summary lives — it
 * splits the figure into "Due now" vs "Pay-later (not yet due)"
 * buckets and provides per-row pay actions, and it surfaces ledger
 * charges (account adjustments / credits) on top of event entry fees,
 * so the badge deep-links there rather than to Event History. This
 * badge is the "you-must-act" alarm, not the running tab.
 *
 * Mounted only when the user is authenticated so the underlying query
 * doesn't fire during sign-out / pre-auth bootstrap. Renders nothing
 * when the balance is zero, loading, or errored — the header must
 * never block on this fetch.
 */

import { CircleDollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOutstandingBalance } from '@/api/extensions/useRegistrationOps';
import { formatCurrency } from '@/utils/formatters';

export function OutstandingBalanceButton() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useOutstandingBalance();

  if (isLoading || isError) return null;
  // Use `total_due_now` (the enforceable subset) so pre-entry-close
  // pay-later seats stay quiet here — the user is intentionally
  // deferring those. Rejection-induced surcharges, post-entry-close
  // pending fees, and confirmed-with-supplemental balances all show
  // up. Falls back to `total_outstanding` for older backend builds
  // still serving only the legacy field name. Manual-ledger debt is
  // ALWAYS due-now (flat committee charges have no entry-close grace),
  // so it's added on top of whatever the registration side reports.
  const eventOwed = data?.total_due_now ?? data?.total_outstanding ?? 0;
  const manualOwed = data?.manual_charges?.net_owed ?? 0;
  const owed = eventOwed + manualOwed;
  if (owed <= 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/profile?tab=financials')}
      className="inline-flex items-center gap-1.5 rounded-md bg-rose-500/15 hover:bg-rose-500/25 text-rose-100 border border-rose-400/40 px-2.5 py-1 text-xs font-semibold transition-colors max-w-[160px] truncate"
      aria-label={`You owe ${formatCurrency(owed, 2)} — open Financials`}
      title={`You owe ${formatCurrency(owed, 2)} — click to pay`}
    >
      <CircleDollarSign className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{formatCurrency(owed, 2)} owed</span>
    </button>
  );
}
